import { path, zipper, isDefined } from "../utils";
import { ema } from "./index";
import { PMO as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = { ...defaultOptions };

  function calculator(data = []) {
    const { sourcePath, smoothingPeriod, doubleSmoothingPeriod, signalPeriod } =
      options;

    if (data.length < 2) {
      return [];
    }

    const getPrice = path(sourcePath);

    // Step 1: Calculate ROC × 10 (1000 × rate of change)
    const rocx10 = [];
    for (let i = 0; i < data.length; i++) {
      if (i === 0) {
        rocx10.push(undefined);
        continue;
      }

      const currentPrice = getPrice(data[i]);
      const prevPrice = getPrice(data[i - 1]);

      if (currentPrice == null || prevPrice == null || prevPrice === 0) {
        rocx10.push(undefined);
        continue;
      }

      // ROCx10 = 1000 × ((Price[i] / Price[i-1]) - 1)
      const roc = 1000 * (currentPrice / prevPrice - 1);
      rocx10.push(roc);
    }

    // Step 2: First EMA (Smoothing Period - 1)
    const firstEmaPeriod = Math.max(1, smoothingPeriod - 1);
    const emaCalculator1 = ema().options({
      windowSize: firstEmaPeriod,
      sourcePath: "rocx10",
    });

    const rocData = data.map((d, i) => ({ ...d, rocx10: rocx10[i] }));
    const emaResults1 = emaCalculator1(rocData);

    // Step 3: Second EMA (Double Smoothing Period - 1) → PMO
    const doubleEmaPeriod = Math.max(1, doubleSmoothingPeriod - 1);
    const emaCalculator2 = ema().options({
      windowSize: doubleEmaPeriod,
      sourcePath: "ema1",
    });

    const ema1Data = data.map((d, i) => ({ ...d, ema1: emaResults1[i] }));
    const pmoResults = emaCalculator2(ema1Data);

    // Step 4: Signal Line (EMA of PMO)
    const signalCalculator = ema().options({
      windowSize: signalPeriod,
      sourcePath: "pmo",
    });

    const pmoData = data.map((d, i) => ({ ...d, pmo: pmoResults[i] }));
    const signalResults = signalCalculator(pmoData);

    // Combine pmo and signal arrays into array of objects
    const zip = zipper().combine((pmo, pmoSignal) => ({
      pmo: isDefined(pmo) ? pmo : undefined,
      pmoSignal: isDefined(pmoSignal) ? pmoSignal : undefined,
    }));

    return zip(pmoResults, signalResults);
  }

  calculator.undefinedLength = function () {
    const { smoothingPeriod, doubleSmoothingPeriod, signalPeriod } = options;
    // Total undefined length is the sum of all EMA windows
    return smoothingPeriod - 1 + (doubleSmoothingPeriod - 1) + signalPeriod;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return { ...options };
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
